import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useEffect } from 'react'
import fetchBusinessEmployees from 'actions/businessTraveller/fetchBusinessEmployees'
import fetchBusinessApprovalRequests from 'actions/businessTraveller/fetchBusinessApprovalRequests'
import moment from 'moment'

interface Params {
  currentRegionCode: string,
  isEnabled?: boolean,
}

function useBusinessTravellerTripRequests(params: Params) {
  const { currentRegionCode, isEnabled = true } = params

  const appDispatch = useAppDispatch()
  const businessId = useAppSelector(state => state.businessTraveller.currentBusinessAccount?.business?.id)
  const employeeId = useAppSelector(state => state.businessTraveller.currentBusinessAccount?.employee?.id)
  const approvalRequests = useAppSelector(
    state => state.businessTraveller.approvalRequests?.request || [],
  )
  const employees = useAppSelector(state => state.businessTraveller.employees.all)
  const status = useAppSelector(state => state.businessTraveller.approvalRequests?.status)

  useEffect(() => {
    if (isEnabled && businessId) {
      appDispatch(fetchBusinessEmployees(businessId))
    }
  }, [appDispatch, businessId, isEnabled])

  useEffect(() => {
    if (isEnabled && businessId && currentRegionCode && employeeId && employees) {
      const employeesArray: Array<App.BusinessTraveller.EmployeeFromMeEndpoint> = Object.values(employees)
      appDispatch(fetchBusinessApprovalRequests(businessId, employeeId, currentRegionCode, employeesArray))
    }
  }, [appDispatch, businessId, employeeId, currentRegionCode, employees, isEnabled])

  const approvalRequestsWithUpcomingStatus = approvalRequests.map(request => {
    const lastUpdatedDaysAgo = request.updatedAt ?
        moment().diff(moment(request.updatedAt), 'days') :
      null

    const isRecent = lastUpdatedDaysAgo !== null && lastUpdatedDaysAgo <= 7
    const isUpcoming =
      request.status === 'REQUESTED' ||
      request.status === 'APPROVED' ||
      ((request.status === 'DENIED' || request.status === 'EXPIRED') && isRecent)
    const isApproved = request.status === 'APPROVED'

    return { request, isUpcoming, isApproved }
  })

  const upcomingApprovals = approvalRequestsWithUpcomingStatus.filter(request => request.isUpcoming).map(request => request.request)
  const completedApprovals = approvalRequestsWithUpcomingStatus.filter(request => !request.isUpcoming).map(request => request.request)
  const approvedApprovals = approvalRequestsWithUpcomingStatus.filter(request => request.isApproved).map(request => request.request)

  return {
    upcoming: upcomingApprovals,
    completed: completedApprovals,
    approved: approvedApprovals,
    status,
  }
}

export default useBusinessTravellerTripRequests
